import { config } from "@shared/config";

import { ServiceRequestService } from "../services/service-request";
import { api } from "../services/common/base";
import { DoctorService } from "../services/doctor";
import { defaultHeaders } from "../utils";
import { AssistantService } from "../services/assistant";
import { MidwifeService } from "../services/midwife";
import { OrganizationService } from "../services/organization";
import { AccessPolicyService } from "../services/access-policy";
import { UserService } from "../services/user";
import { PatientCheckupService } from "../services/patient-checkup-service";
import { UploadService } from "../services/upload";
import { TransmissionService } from "../services/transmission";
import { PatientService } from "../services/patient";
import { AppointmentService } from "../services/appointment";
import { ChannelService } from "../services/channel";
import { TeleexpertiseService } from "../services/teleexpertise";
import { ContactService } from "../services/contact";
import { DispatchOperatorService } from "../services/dispatch-operator";
import { DispatchManagerService } from "../services/dispatch-manager";
import { SearchService } from "../services/search";
import { DispatchService } from "../services/dispatch";
import { IntegrationService } from "../services/integration";
import { AppointmentAsyncService } from "../services/appointment-async";
import { QuestionnaireResponseService } from "../services/questionnaire-response";
import { AdminService } from "../services/admin";
import { DispatchOrganizationService } from "../services/dispatch-organization";
import { InventoryService } from "../services/inventory";

export const useApi = () => {
  return {
    baseUrl: config.VITE_MC_API,
    defaultHeaders: defaultHeaders(),
    doctor: new DoctorService(),
    serviceRequest: new ServiceRequestService(),
    assistant: new AssistantService(),
    midwife: new MidwifeService(),
    organization: new OrganizationService(),
    accessPolicy: new AccessPolicyService(),
    user: new UserService(),
    upload: new UploadService(),
    patientCheckUp: new PatientCheckupService(),
    transmission: new TransmissionService(),
    patient: new PatientService(),
    appointment: new AppointmentService(),
    channel: new ChannelService(),
    search: new SearchService(),
    schedule: {
      getSchedule: ({ scheduleId, startDate, endDate }, opts = {}) =>
        api
          .get(`/schedule/${scheduleId}`, {
            params: {
              startDate,
              endDate,
            },
            ...opts,
          })
          .then((res) => res.data),
      findScheduleByLocation: ({
        userType,
        longitude,
        latitude,
        start,
        end,
        distance,
      }) =>
        api
          .get(`/user-schedule/${userType}`, {
            params: {
              longitude,
              latitude,
              start,
              end,
              distance,
            },
          })
          .then((res) => res.data),
    },
    teleexpertise: new TeleexpertiseService(),
    contact: new ContactService(),
    dispatchManager: new DispatchManagerService(),
    dispatchOperator: new DispatchOperatorService(),
    dispatch: new DispatchService(),
    integration: new IntegrationService(),
    ota: {
      get: (version: string | "latest") =>
        api.get(`ota/${version}`).then((res) => res.data),
    },
    userSchedule: {
      getDailyAvailability: (userType, id, start, duration) =>
        api
          .get(`/user-schedule/${userType}/${id}/daily-availability`, {
            params: {
              start,
              duration,
            },
          })
          .then((res) => res.data),
      findAvailableSlots: (params: {
        longitude: number;
        latitude: number;
        date: Date | string;
        distance?: number;
        duration?: number;
        patientId?: string;
        assistantId?: string;
        midwifeId?: string;
        doctorId?: string;
        organizationId?: string;
        userTypes: string[];
      }) =>
        api
          .get("/user-schedule/find-available-slots", {
            params,
          })
          .then((res) => res.data),
      findDoctorsSlots: (params: {
        date: Date | string;
        duration?: number;
        doctorId?: string;
      }) =>
        api
          .get("/user-schedule/appointment-builder/find-doctors-slots", {
            params,
          })
          .then((res) => res.data),

      assignAssistant: (params: {
        start: Date | string;
        end: Date | string;
        longitude: number;
        latitude: number;
        distance?: number;
      }) =>
        api
          .get("/user-schedule/appoitment-builder/assign-assistant", {
            params,
          })
          .then((res) => res.data),
    },
    ordoclic: {
      getAuthCode: (payload: {
        ordoclicPatientId: string;
        patientNir: string;
      }) => api.post("/ordoclic/auth-code", payload).then((res) => res.data),
      getPrescriptionFile: (payload: {
        ordoclicPatientId: string;
        ordoclicPrescriptionId: string;
      }) => api.post("/ordoclic/prescription", payload).then((res) => res.data),
      getDocumentFile: (payload: {
        ordoclicPatientId: string;
        ordoclicDocumentId: string;
      }) => api.post("/ordoclic/document", payload).then((res) => res.data),
    },
    asyncAppointments: new AppointmentAsyncService(),
    questionnaireResponse: new QuestionnaireResponseService(),
    admin: new AdminService(),
    dispatchOrganization: new DispatchOrganizationService(),
    inventory: new InventoryService(),
    support: {
      create: (data: {
        subject: string;
        message: string;
        url?: string;
        sessionUrl?: string;
      }) => api.post("/support", data).then((res) => res.data),
    },
  };
};
